// MedicalHistoryCard.js
import React, { useEffect, useState } from 'react';
import { Plus, X } from 'react-feather';
import '../../styles/cardStyles.css';
import { useDispatch, useSelector } from 'react-redux';
import { UsersMedicalHistoryGet } from 'apiServices/reducer/apiService';
import MedicalHistoryModal from '../dialogs/dashboard/addMedicalHistory';
import ManageMedicalHistory from '../dialogs/dashboard/ManageMedicalHistory';

const MedicalHistoryCard = ({
    showAddButton,
    onAdd,
    onRemove,
    widgetName,
    draggable = false,
    onDragStart,
    selectedFamilyMember
}) => {
    const dispatch = useDispatch();
    const userData = useSelector((state) => state.apiService.login);
    const isProvider = userData.user_type_name === 'Provider';
    
    // Use selectedFamilyMember's user_id if available, otherwise use current user's ID
    const userId = selectedFamilyMember?.user_id || (isProvider ? localStorage.getItem('patientUserId') : userData.user_id);
    
    const token = useSelector((state) => state.apiService?.token);
    const [medicalData, setMedicalData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showManageModal, setShowManageModal] = useState(false);

    const fetchMedicalHistory = async () => {
        try {
            const response = await dispatch(
                UsersMedicalHistoryGet({
                    UserID: userId,
                    token,
                    medical_history: 'Y',
                    surgical_history: 'Y',
                    dental_history: 'Y',
                })
            );
            if (response?.payload && Array.isArray(response.payload)) {
                setMedicalData(response.payload);
            } else {
                setMedicalData([]);
            }
        } catch (err) {
            console.error('Failed to fetch medical history:', err);
            setMedicalData([]);
        }
    };

    useEffect(() => {
        if (userId && token) {
            fetchMedicalHistory();
        }
    }, [userId, token, selectedFamilyMember]);

    const handleAddClick = () => {
        const latestRecord = medicalData.length > 0 ? medicalData[0] : null;
        setSelectedRow(latestRecord);
        setShowModal(true);
    };

    const handleEditClick = () => {
        const latestRecord = medicalData.length > 0 ? medicalData[0] : null;
        setSelectedRow(latestRecord);
        setShowManageModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const formatHistoryForDisplay = (text) => {
        if (!text) return <i className="text-muted">N/A</i>;

        const lines = text
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);

        return (
            <ul className="mb-0 ps-3">
                {lines.map((line, idx) => (
                    <li key={idx}>{line}</li>
                ))}
            </ul>
        );
    };


    return (
        <>
            <div
                className="cardwidget cardwidgetHeight d-flex flex-column shadow-lg shadow-sm border-0 mb-3 rounded-3"
                draggable={draggable}
                onDragStart={onDragStart}
            >
                <div className="cardwidget-header cardwidgehtHeaderBG d-flex justify-content-between align-items-center px-3 py-1">
                    <h6 className="mb-0  text-capitalize">{widgetName || 'Medical History'}</h6>
                    {showAddButton ? (
                        <button className="btn btn-sm  px-0" onClick={onAdd} title="Add to Dashboard">
                            <Plus size={15} />
                        </button>
                    ) : (
                        <button className="btn btn-sm px-0" onClick={onRemove} title="Remove from Dashboard">
                            <X size={15} />
                        </button>
                    )}
                </div>

                <div className="cardwidget-body rounded px-3 py-2">
                    {medicalData.length > 0 ? (
                        <div className="history-content p-2 rounded">
                            {medicalData[0].medical_history && medicalData[0].medical_history.trim() && (
                                <div className="history-section mb-3">
                                    <h6 className="fw-bold text-capitalize">Medical History:</h6>
                                    <div className="fw-normal py-2 medical-history-content">{formatHistoryForDisplay(medicalData[0].medical_history)}</div>
                                </div>
                            )}
                            {medicalData[0].surgical_history && medicalData[0].surgical_history.trim() && (
                                <div className="history-section mb-3">
                                    <h6 className="fw-bold text-capitalize">Surgical History:</h6>
                                    <div className="fw-normal py-2 medical-history-content">{formatHistoryForDisplay(medicalData[0].surgical_history)}</div>
                                </div>
                            )}
                            {medicalData[0].dental_history && medicalData[0].dental_history.trim() && (
                                <div className="history-section mb-3">
                                    <h6 className="fw-bold text-capitalize">Dental History:</h6>
                                    <div className="fw-normal py-2 medical-history-content">{formatHistoryForDisplay(medicalData[0].dental_history)}</div>
                                </div>
                            )}
                            {(!medicalData[0].medical_history || !medicalData[0].medical_history.trim()) && 
                             (!medicalData[0].surgical_history || !medicalData[0].surgical_history.trim()) && 
                             (!medicalData[0].dental_history || !medicalData[0].dental_history.trim()) && (
                                <p className="text-muted">No medical history data available.</p>
                            )}
                            <div className="history-meta">
                                {medicalData[0].DateCreated && (
                                    <small className="text-secondary d-block">
                                        Date: {new Date(medicalData[0].DateCreated).toLocaleDateString()}
                                    </small>
                                )}
                                {medicalData[0].CreatedBy && (
                                    <small className="text-secondary d-block">
                                        By: {medicalData[0].CreatedBy}
                                    </small>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted">No medical history found.</p>
                    )}
                </div>

                {!isProvider &&
                    <>

                        <div className="cardwidget-footer bg-white border-top d-flex justify-content-start px-3 py-2">
                            <button className="btn btn-link modalfooterclickeventtext p-0 me-2 text-capitalize cursor-pointer" onClick={handleAddClick}>
                                Add
                            </button>
                            <span className="text-muted">|</span>
                            <button className="btn btn-link modalfooterclickeventtext p-0 ms-2 text-capitalize cursor-pointer" onClick={handleEditClick}>
                                Edit
                            </button>
                        </div>
                    </>
                }
            </div>

            <MedicalHistoryModal
                show={showModal}
                handleClose={handleModalClose}
                rowData={selectedRow}
                refreshMedicalHistory={fetchMedicalHistory}
                userId={userId}
            />
            <ManageMedicalHistory
                show={showManageModal}
                onHide={() => setShowManageModal(false)}
                medicalData={selectedRow}
                refreshData={fetchMedicalHistory}
                userId={userId}
            />
        </>
    );
};

export default MedicalHistoryCard;